import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { useActiveContextReady } from './useActiveContextReady';
import { apiErrorMessage } from '../utils/apiError';
import { config } from '../config';

export interface DeviceConnectionMethod {
  id: string;
  deviceId: string;
  sessionType: string;
  host: string;
  port: number | null;
  transportId: string | null;
  credentialId: string | null;
  priority: number;
  enabled: boolean;
  command: string | null;
  /** Vendor-profile `service` role — see ConnectionMethodSummary.service. */
  service: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConnectionMethodRequest {
  sessionType: string;
  host: string;
  port?: number | null;
  transportId?: string | null;
  credentialId?: string | null;
  priority?: number;
  enabled?: boolean;
  command?: string | null;
  /** Optional `service` role. Only meaningful for http(s) methods, where it
   *  separates a browsable UI ("http"/"cgi") from a machine API
   *  ("rest"/"http_api") — a device can expose both over https. Omit to leave
   *  the role unrecorded, which falls back to the vendor profile. */
  service?: string | null;
}

interface ConnectionMethodListResponse {
  connectionMethods: DeviceConnectionMethod[];
}

const backendApi = config.backendApi;

export function useDeviceConnectionMethods(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<DeviceConnectionMethod[]>({
    queryKey: ['connectionMethods', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<ConnectionMethodListResponse>(
        `${backendApi}/api/devices/${deviceId}/connection-methods`,
        { withCredentials: true }
      );
      return response.data.connectionMethods;
    },
    enabled: !!deviceId && contextReady,
  });
}

export function useCreateConnectionMethod(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateConnectionMethodRequest) => {
      const response = await axios.post<DeviceConnectionMethod>(
        `${backendApi}/api/devices/${deviceId}/connection-methods`,
        data,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectionMethods', deviceId] });
      // The device response derives `protocolSetup` from its connection methods
      // — `webUiBrowsable` needs an http(s) one — so adding a method changes
      // which tabs the device page renders. Without this the Web UI tab stays
      // hidden until `useDeviceDetails`' 30s staleTime lapses or the user
      // reloads, which reads as "the tab is missing" rather than "stale".
      void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    },
  });
}

export function useDeleteConnectionMethod(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (methodId: string) => {
      await axios.delete(
        `${backendApi}/api/devices/${deviceId}/connection-methods/${methodId}`,
        { withCredentials: true }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectionMethods', deviceId] });
      // Removing the last http(s) method retires the Web UI tab — see
      // useCreateConnectionMethod.
      void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    },
  });
}

export interface UpdateConnectionMethodRequest {
  sessionType?: string;
  host?: string;
  port?: number | null;
  transportId?: string | null;
  credentialId?: string | null;
  priority?: number;
  enabled?: boolean;
  command?: string | null;
  /** See CreateConnectionMethodRequest.service. */
  service?: string | null;
}

export function useUpdateConnectionMethod(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ methodId, data }: { methodId: string; data: UpdateConnectionMethodRequest }) => {
      const response = await axios.put<DeviceConnectionMethod>(
        `${backendApi}/api/devices/${deviceId}/connection-methods/${methodId}`,
        data,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectionMethods', deviceId] });
      // An edit can change `sessionType` or `credentialId`, both of which feed
      // `protocolSetup` — see useCreateConnectionMethod.
      void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    },
  });
}

export interface ConnectionTestResult {
  methodName: string;
  success: boolean;
  latencyMs: number;
  error: string;
  deviceConnectionMethodId: string;
}

interface ConnectionTestResponse {
  deviceId: string;
  results: ConnectionTestResult[];
}

/**
 * Trigger a live, functional connection attempt. With no argument it tests
 * every configured method; pass a `methodId` to test just one (the guided
 * Connections UI's per-protocol "Test"). The gateway runs a real protocol
 * probe (eAPI runCmds / NETCONF hello / SNMP get / SSH connect / CGI fetch)
 * and records the outcome to connection_method_health, so both the health and
 * connections views refresh once the gateway's next report lands.
 */
export function useTestConnections(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (methodId?: string) => {
      const response = await axios.post<ConnectionTestResponse>(
        `${backendApi}/api/devices/${deviceId}/connection-test`,
        {},
        {
          withCredentials: true,
          params: methodId ? { method_id: methodId } : undefined,
        }
      );
      return response.data.results;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['device-health', deviceId] });
      void queryClient.invalidateQueries({ queryKey: ['device-connections', deviceId] });
    },
  });
}

export function useToggleConnectionMethodEnabled(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ methodId, enabled }: { methodId: string; enabled: boolean }) => {
      const response = await axios.put<DeviceConnectionMethod>(
        `${backendApi}/api/devices/${deviceId}/connection-methods/${methodId}`,
        { enabled },
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connectionMethods', deviceId] });
    },
    // The switch moves the instant it is clicked and snaps back on the next
    // refetch. Without this the user sees a toggle that silently undoes itself
    // and no reason why — a controller-owned method answers 409, naming the
    // integration that owns it and where the setting actually lives.
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to change the connection method.'));
    },
  });
}
